import { CONSTANTS } from "@/constants";
import { IGetUserFlowByUrlUserSteps } from "@/hooks";
import { ESize } from "@/interfaces/enums";
import { UserType } from "@/interfaces/types";
import { IDBContentPage } from "@netfront/ekardo-content-library";
import { jwtDecode } from "jwt-decode";

export const getValidClassNames = (object: { [key: string]: boolean }) => {
  return Object.keys(object)
    .filter((key) => object[key])
    .join(' ');
};

export const getSizeClass = (className: string, size: ESize): string => {
  return `${className}-${size}`;
};

export const capitalise = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export const getFlattenContentPages = (contentPages: IDBContentPage[] | undefined): IDBContentPage[] => {
  if (!contentPages) return [];
  
  return contentPages?.reduce((acc, contentPage) => {
    acc.push(contentPage);
    
    if (contentPage.contentPageChildren && contentPage.contentPageChildren.length) {
      acc.push(...getFlattenContentPages(contentPage.contentPageChildren));
    }

    return acc;
  }, [] as IDBContentPage[]);
};

export const getParentModuleConstants = (stepId: number) => {
  return Object.values(CONSTANTS.USERFLOW.PARENT.STEPS.MODULES).find(step => step.ID === stepId);
};

export const getParentGoalConstants = (stepId: number) => {
  return Object.values(CONSTANTS.USERFLOW.PARENT.STEPS.GOALS).find(step => step.ID === stepId);
};

export const getParentFeedbackConstants = (stepId: number) => {
  return Object.values(CONSTANTS.USERFLOW.PARENT.STEPS.FEEDBACK).find(step => step.ID === stepId);
};

export const getParentSummaryConstants = (stepId: number) => {
  return Object.values(CONSTANTS.USERFLOW.PARENT.STEPS.SUMMARIES).find(step => step.ID === stepId);
};

export const getUserTypeRedirect = (userTypeId?: number) => {
  switch(userTypeId) {
    case CONSTANTS.USER_TYPE.PARENT.ID:
      return '/parent'
    case CONSTANTS.USER_TYPE.STUDENT.ID:
      return '/student'
    case CONSTANTS.USER_TYPE.TEACHER.ID:
      return '/teacher'
    default:
      return '/student'
  }
};

export const getParentModuleIds = () => Object.values(CONSTANTS.USERFLOW.PARENT.STEPS.MODULES).map(({ ID }) => ID);

export const getGoalsStepIds = () => Object.values(CONSTANTS.USERFLOW.PARENT.STEPS.GOALS).map(({ ID }) => ID);

export const getFeedbackStepIds = () => Object.values(CONSTANTS.USERFLOW.PARENT.STEPS.FEEDBACK).map(({ ID }) => ID);

export const getSummaryStepIds = () => Object.values(CONSTANTS.USERFLOW.PARENT.STEPS.SUMMARIES).map(({ ID }) => ID);

export const getSummaryByModule = (summaries: IGetUserFlowByUrlUserSteps[], stepId: number): IGetUserFlowByUrlUserSteps | undefined => 
  summaries.find(({ id }) => stepId === getParentSummaryConstants(id)?.MODULE.ID);

export const ensureAustralianAreaCode = (phoneNumber: string) => {
  let cleanedNumber = phoneNumber.replace(/[^\d+]/g, '');

  if (/^\+61/.test(cleanedNumber)) {
      return cleanedNumber;
  }
  
  if (/^0[23478]/.test(cleanedNumber)) {
      return cleanedNumber.replace(/^0/, '+61');
  }

  return cleanedNumber;
}

export const getModulePageUrl = (step: IGetUserFlowByUrlUserSteps): string => {
  if (!step.contentGroup?.contentPages?.length) {
    throw new Error('Invalid step: missing content pages');
  }

  return `${
    step.isComplete
      ? step.contentGroup?.contentPages[0].url
      : step.lastViewedContentPageCompletion
        ? step.lastViewedContentPageCompletion.contentPage.url
        : step.contentGroup?.contentPages[0].url
  }`;
};

export const getCompletedLessonsCount = (lessons: IGetUserFlowByUrlUserSteps[]) => 
  lessons.filter(({ isComplete }) => isComplete).length;

export const getProgressCompletionPercentage = (contentPageId: number, contentPages: IDBContentPage[]) => {
    if(!contentPages) return 0;

    const currentIndex = getFlattenContentPages(contentPages)?.findIndex(({ id }) => id === contentPageId);
    const total = getFlattenContentPages(contentPages)?.length - 1;

    if(!total) {
      return 0;
    }

    return Math.round(currentIndex / total * 100);
  };

  export const getS3KeyFromUrl = (url: string) => {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.pathname.startsWith('/') ? parsedUrl.pathname.slice(1) : parsedUrl.pathname;
    } catch (error) {
      console.error("Invalid URL:", error);
      return null;
    }
  };

  export const isModuleOpen = (
    startingModule: IGetUserFlowByUrlUserSteps,
    module: IGetUserFlowByUrlUserSteps
  ): { isOpen: boolean; openDate?: Date } => {
    if (startingModule.id === module.id) {
      return { isOpen: true };
    }
  
    const startingDate = new Date(startingModule.lastTracking?.startedLongDate as string);
    if (isNaN(startingDate.getTime())) {
      return { isOpen: false };
    }
  
    const moduleIndex = module.sort;
    const moduleOpenDate = new Date(startingDate);
    moduleOpenDate.setDate(startingDate.getDate() + moduleIndex * 7);
  
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    moduleOpenDate.setHours(0, 0, 0, 0);
  
    return {
      isOpen: today >= moduleOpenDate,
      openDate: moduleOpenDate
    };
  };

  export const isTokenExpired = (token: string | undefined) => {
      if (!token) return true;
    
      const { exp } = jwtDecode(token);
      const now = Date.now() / 1000;
      return Number(exp) < now;
    }


export const shouldIncludePhone = (userTypeId: number | undefined) => false; //No user type requires in phase 3

export const extractS3Info = (presignedUrl: string) => {
  const url = new URL(presignedUrl);

  const bucket = url.hostname.split('.')[0];

  const key = decodeURIComponent(url.pathname.substring(1));

  return { bucket, key };
};